import { Link } from 'react-router-dom';
import { ArrowUpRight, TrendDown, TrendUp } from '@phosphor-icons/react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';

import { MoneyDisplay } from '../common/MoneyDisplay';
import { useExpenseTimelineSeries } from '../expenses/ExpenseTimelineChart';
import pt from '../../locales/pt';
import { paths } from '../../routes/paths';
import { cn } from '../../utils/cn';

const SPARKLINE_CYCLES = 6;

/**
 * Sparkline compacta (ds/card/metric-sparkline no .pen) — só para este card do
 * Início. O gráfico completo (eixos/grid/média) de `ExpenseTimelineChart.jsx`
 * é partilhado com outros ecrãs e não é tocado aqui.
 */
function MiniSparkline({ data }) {
  return (
    <div className="h-16 w-full" aria-hidden>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="expense-sparkline-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7C9E6F" stopOpacity={0.45} />
              <stop offset="100%" stopColor="#7C9E6F" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="expenses"
            stroke="#5F7E54"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="url(#expense-sparkline-fill)"
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Card do Início: evolução de despesas por ciclo (ds/card/metric-sparkline no .pen).
 * Só renderiza quando há ≥2 ciclos e pelo menos um com gasto.
 */
export function ExpenseTimelineMiniCard({ showLink = true }) {
  const { data, hasData, avg } = useExpenseTimelineSeries({ maxCycles: SPARKLINE_CYCLES });

  if (!hasData) return null;

  const latest = data.length ? data[data.length - 1].expenses : null;
  const deltaPct = latest != null && avg > 0 ? ((latest - avg) / avg) * 100 : null;
  const isDecline = deltaPct != null && deltaPct < 0;
  const DeltaIcon = isDecline ? TrendDown : TrendUp;

  return (
    <div
      className="flex flex-col gap-space-4 rounded-ds-lg border border-border bg-card p-space-5 shadow-[0_6px_16px] shadow-glass-shadow/[0.12] dark:shadow-glass-shadow/[0.45]"
      data-testid="expense-timeline-card"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-type-title-card font-semibold text-card-foreground">
          {pt.dashboard.charts.expenseEvolutionTitle}
        </h3>
        {deltaPct != null ? (
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-space-1 rounded-ds-full px-space-2-5 py-space-1',
              isDecline
                ? 'bg-success-tint text-success-700 dark:text-success-50'
                : 'bg-warning-tint text-warning-700 dark:text-warning-50',
            )}
          >
            <DeltaIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="text-type-chip font-semibold">
              {deltaPct >= 0 ? '+' : ''}
              {deltaPct.toFixed(1)} %
            </span>
          </span>
        ) : showLink ? (
          <Link
            to={paths.expenses}
            className="shrink-0 rounded-lg p-1 text-muted-foreground/70 transition hover:bg-muted hover:text-muted-foreground"
            aria-label={pt.expenses.title}
          >
            <ArrowUpRight className="h-4 w-4" aria-hidden />
          </Link>
        ) : null}
      </div>
      {avg > 0 ? (
        <div className="flex items-end gap-space-3">
          <MoneyDisplay value={avg} size="xl" className="type-display-kpi text-card-foreground" />
          <span className="pb-1 text-type-body-sm font-normal text-muted-foreground">
            {pt.dashboard.charts.expenseEvolutionAvgLabel}
          </span>
        </div>
      ) : null}
      <MiniSparkline data={data} />
      {data.length > 1 ? (
        <div className="type-caption flex justify-between text-muted-foreground">
          <span>{data[0].label}</span>
          <span>{data[data.length - 1].label}</span>
        </div>
      ) : null}
    </div>
  );
}

export default ExpenseTimelineMiniCard;
